// Сервис для работы с API проектов (workspace) и RAG-инстансов
import { DTCORE_API_BASE_URL, apiRequest } from '../config/api';
import { normalizeTableContentPaging } from '../utils/storagePagination';

/**
 * @typedef {Object} Project
 * @property {string} project_id
 * @property {string} db_name
 * @property {string} [name]
 * @property {string} project_title
 * @property {'draft'|'active'|'pause'|'deleted'} status
 * @property {number} [rags_count]
 * @property {string} created_at
 * @property {string} [updated_at]
 */

/**
 * @typedef {Object} RagInstance
 * @property {string} rag_id
 * @property {string} project_id
 * @property {string} [user_id]
 * @property {string} name
 * @property {string} [llm_model]
 * @property {string} [embeddings_model]
 * @property {Object} [config]
 * @property {'draft'|'active'|'deleted'} status
 * @property {string} created_at
 * @property {string} [updated_at]
 */

/** @param {string} projectId @param {string} ragId */
function ragBasePath(projectId, ragId) {
  return `${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/rags/${ragId}`;
}

/**
 * Получить список проектов
 * @param {Object} params
 * @param {string} [params.status] - фильтр по статусу (active, draft, ...)
 * @param {string} [params.category_type] - фильтр по типу категории (rag, ...)
 */
export async function getProjects(params = {}) {
  const { status, category_type, db_name, project_name } = params;
  const normalizedProjectName = db_name || project_name;
  
  const url = new URL(`${DTCORE_API_BASE_URL}/api/v1/projects`);
  
  if (status) url.searchParams.set('status', status);
  if (category_type) url.searchParams.set('category_type', category_type);
  if (normalizedProjectName) url.searchParams.set('project_name', normalizedProjectName);
  
  return await apiRequest(url.toString(), {
    method: 'GET',
  });
}

/**
 * Получить проект по ID
 * @param {string} projectId
 */
export async function getProjectById(projectId) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}`, {
    method: 'GET',
  });
}

/**
 * Создать проект
 * @param {Object} data
 */
export async function createProject(data) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Обновить проект
 * @param {string} projectId
 * @param {Object} data
 */
export async function updateProject(projectId, data) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Удалить проект
 * @param {string} projectId
 */
export async function deleteProject(projectId) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}`, {
    method: 'DELETE',
  });
}

// --- RAG instances ---

/**
 * Создать RAG-инстанс в проекте
 * @param {string} projectId
 * @param {Object} data - { name, use_defaults?, llm_model?, embeddings_model? }
 * @returns {Promise<RagInstance>}
 */
export async function createRag(projectId, data) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/rags`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Список RAG-инстансов проекта
 * @param {string} projectId
 * @returns {Promise<RagInstance[]>}
 */
export async function getRags(projectId) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/rags`, {
    method: 'GET',
  });
}

/**
 * Получить RAG-инстанс по ID
 * @param {string} projectId
 * @param {string} ragId
 * @returns {Promise<RagInstance>}
 */
export async function getRagById(projectId, ragId) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/rags/${ragId}`, {
    method: 'GET',
  });
}

/**
 * Обновить RAG-инстанс (в т.ч. активация: { status: 'active' })
 * @param {string} projectId
 * @param {string} ragId
 * @param {Object} data
 * @returns {Promise<RagInstance>}
 */
export async function updateRag(projectId, ragId, data) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/rags/${ragId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Удалить RAG-инстанс (soft delete)
 * @param {string} projectId
 * @param {string} ragId
 */
export async function deleteRag(projectId, ragId) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/rags/${ragId}`, {
    method: 'DELETE',
  });
}

/**
 * Получить источники проекта
 * @param {string} projectId
 */
export async function getProjectSources(projectId) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/sources`, {
    method: 'GET',
  });
}

/**
 * Добавить источник к проекту
 * @param {string} projectId
 * @param {Object} data - { source_id: number }
 */
export async function addProjectSource(projectId, data) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/sources`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Удалить источник из проекта
 * @param {string} projectId
 * @param {number} sourceId
 */
export async function removeProjectSource(projectId, sourceId) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/sources/${sourceId}`, {
    method: 'DELETE',
  });
}

/**
 * Получить хранилища проекта
 * @param {string} projectId
 */
export async function getProjectStorages(projectId) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/storages`, {
    method: 'GET',
  });
}

/**
 * Добавить хранилище к проекту
 * @param {string} projectId
 * @param {Object} data - { storage_id: number }
 */
export async function addProjectStorage(projectId, data) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/storages`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Удалить хранилище из проекта
 * @param {string} projectId
 * @param {number} storageId
 */
export async function removeProjectStorage(projectId, storageId) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/storages/${storageId}`, {
    method: 'DELETE',
  });
}

/**
 * Проверить соединение с хранилищем
 * @param {number} storageId
 */
export async function testStorage(storageId) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/storages/${storageId}/test`, {
    method: 'POST',
  });
}

/**
 * Получить список LLM провайдеров
 */
export async function getLLMProviders() {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/models/llm`, {
    method: 'GET',
  });
}

/**
 * Создать LLM провайдер
 * @param {Object} data
 */
export async function createLLMProvider(data) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/models/llm`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Проверить LLM провайдер
 * @param {number} providerId
 */
export async function testLLMProvider(providerId) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/models/llm/${providerId}/test`, {
    method: 'POST',
  });
}

/**
 * Получить список Embeddings провайдеров
 */
export async function getEmbeddingsProviders() {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/models/embeddings`, {
    method: 'GET',
  });
}

/**
 * Создать Embeddings провайдер
 * @param {Object} data
 */
export async function createEmbeddingsProvider(data) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/models/embeddings`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Проверить Embeddings провайдер
 * @param {number} providerId
 */
export async function testEmbeddingsProvider(providerId) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/models/embeddings/${providerId}/test`, {
    method: 'POST',
  });
}

/**
 * Получить типы проектов
 * @param {string} categoryType
 */
export async function getProjectTypes(categoryType = 'rag') {
  const url = new URL(`${DTCORE_API_BASE_URL}/api/v1/projects/types`);
  url.searchParams.set('category_type', categoryType);
  
  return await apiRequest(url.toString(), {
    method: 'GET',
  });
}

/**
 * Обнаружить чаты Telegram-бота (через getUpdates)
 * Работает только до привязки бота (до вызова /telegram/bind)
 * @param {string} projectId
 * @param {string} ragId
 * @param {string} token - Telegram-токен бота
 */
export async function detectTelegramChats(projectId, ragId, token) {
  return await apiRequest(`${ragBasePath(projectId, ragId)}/telegram/detect-chats`, {
    method: 'POST',
    body: JSON.stringify({ telegram_chat_token: token }),
  });
}

/**
 * Привязать Telegram бота
 * @param {string} projectId
 * @param {string} ragId
 * @param {Object} data
 * @param {Object} [options]
 * @param {boolean} [options.forceRebind=false]
 */
export async function bindTelegramBot(projectId, ragId, data, options = {}) {
  const url = new URL(`${ragBasePath(projectId, ragId)}/telegram/bind`);
  if (options.forceRebind) {
    url.searchParams.set('force_rebind', 'true');
  }

  return await apiRequest(url.toString(), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Получить статус Telegram бота
 * @param {string} projectId
 * @param {string} ragId
 */
export async function getTelegramBotStatus(projectId, ragId) {
  return await apiRequest(`${ragBasePath(projectId, ragId)}/telegram/status`, {
    method: 'GET',
  });
}

/**
 * Получить список Telegram ботов аккаунта
 */
export async function getAccountTelegramBots() {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/telegram/bots`, {
    method: 'GET',
  });
}

/**
 * Отвязать Telegram бота
 * @param {string} projectId
 * @param {string} ragId
 */
export async function unbindTelegramBot(projectId, ragId) {
  return await apiRequest(`${ragBasePath(projectId, ragId)}/telegram/unbind`, {
    method: 'DELETE',
  });
}

/**
 * Получить whitelist chat_id для Telegram-бота RAG
 * @param {string} projectId
 * @param {string} ragId
 */
export async function getTelegramChatsWhitelist(projectId, ragId) {
  return await apiRequest(`${ragBasePath(projectId, ragId)}/telegram/chats/whitelist`, {
    method: 'GET',
  });
}

/**
 * Добавить chat_id в whitelist Telegram-бота RAG
 * @param {string} projectId
 * @param {string} ragId
 * @param {string} chatId
 */
export async function addTelegramChatToWhitelist(projectId, ragId, chatId) {
  return await apiRequest(`${ragBasePath(projectId, ragId)}/telegram/chats/whitelist`, {
    method: 'POST',
    body: JSON.stringify({ chat_id: chatId }),
  });
}

/**
 * Удалить chat_id из whitelist Telegram-бота RAG
 * @param {string} projectId
 * @param {string} ragId
 * @param {string} chatId
 */
export async function removeTelegramChatFromWhitelist(projectId, ragId, chatId) {
  const encodedChatId = encodeURIComponent(chatId);
  return await apiRequest(`${ragBasePath(projectId, ragId)}/telegram/chats/whitelist/${encodedChatId}`, {
    method: 'DELETE',
  });
}

/**
 * Создать API ключ RAG
 * @param {string} projectId
 * @param {string} ragId
 * @param {Object} data - { key_name: string }
 */
export async function createProjectApiKey(projectId, ragId, data) {
  return await apiRequest(`${ragBasePath(projectId, ragId)}/api-keys`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Получить список API ключей RAG
 * @param {string} projectId
 * @param {string} ragId
 */
export async function getProjectApiKeys(projectId, ragId) {
  return await apiRequest(`${ragBasePath(projectId, ragId)}/api-keys`, {
    method: 'GET',
  });
}

/**
 * Отозвать API ключ RAG
 * @param {string} projectId
 * @param {string} ragId
 * @param {number} keyId
 */
export async function revokeProjectApiKey(projectId, ragId, keyId) {
  return await apiRequest(`${ragBasePath(projectId, ragId)}/api-keys/${keyId}`, {
    method: 'DELETE',
  });
}

/**
 * Получить endpoint для чата RAG
 * @param {string} projectId
 * @param {string} ragId
 */
export async function getProjectChatEndpoint(projectId, ragId) {
  return await apiRequest(`${ragBasePath(projectId, ragId)}/chat/endpoint`, {
    method: 'GET',
  });
}

/**
 * Получить коннекторы проекта
 * @param {string} projectId
 */
export async function getProjectConnectors(projectId) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/connectors`, {
    method: 'GET',
  });
}

/**
 * Создать недостающие коннекторы
 * @param {string} projectId
 */
export async function ensureProjectConnectors(projectId) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/connectors/ensure`, {
    method: 'POST',
  });
}

/**
 * Получить таблицы хранилища проекта
 * @param {string} projectId
 */
export async function getProjectTables(projectId) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/tables`, {
    method: 'GET',
  });
}

/**
 * Получить схему конкретной таблицы проекта
 * @param {string} projectId
 * @param {string} tableNameRaw - table_name в формате schema.table (raw, без encode)
 */
export async function getProjectTableSchema(projectId, tableNameRaw) {
  return await apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/tables/${tableNameRaw}/schema`, {
    method: 'GET',
  });
}

/**
 * Получить preview контента таблицы проекта
 * @param {string} projectId
 * @param {string} tableNameRaw - table_name в формате schema.table (raw, без encode)
 * @param {Object} [params]
 * @param {number} [params.limit=20] - допустимые: 20, 40, 100
 * @param {number} [params.offset=0]
 */
export async function getProjectTableContent(projectId, tableNameRaw, params = {}) {
  const { limit, offset } = normalizeTableContentPaging(params.limit, params.offset);
  const url = new URL(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/tables/${tableNameRaw}/content`);
  url.searchParams.set('limit', String(limit));
  url.searchParams.set('offset', String(offset));
  return await apiRequest(url.toString(), {
    method: 'GET',
  });
}

/**
 * Создать ingestion конфиг
 * @param {string} projectId
 * @param {string} ragId
 * @param {Object} data
 */
export async function createIngestionConfig(projectId, ragId, data) {
  return await apiRequest(`${ragBasePath(projectId, ragId)}/config/ingestion`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Получить ingestion конфиги RAG
 * @param {string} projectId
 * @param {string} ragId
 */
export async function getIngestionConfigs(projectId, ragId) {
  return await apiRequest(`${ragBasePath(projectId, ragId)}/config/ingestion`, {
    method: 'GET',
  });
}

/**
 * Создать retrieval конфиг
 * @param {string} projectId
 * @param {string} ragId
 * @param {Object} data
 */
export async function createRetrievalConfig(projectId, ragId, data) {
  return await apiRequest(`${ragBasePath(projectId, ragId)}/config/retrieval`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Получить retrieval конфиги RAG
 * @param {string} projectId
 * @param {string} ragId
 */
export async function getRetrievalConfigs(projectId, ragId) {
  return await apiRequest(`${ragBasePath(projectId, ragId)}/config/retrieval`, {
    method: 'GET',
  });
}

/**
 * Выполнить test-query
 * @param {string} projectId
 * @param {string} ragId
 * @param {Object} data - { query: string, retrieval_config_version: number }
 */
export async function testProjectQuery(projectId, ragId, data) {
  return await apiRequest(`${ragBasePath(projectId, ragId)}/test-query`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Отправить чат-запрос к внутреннему RAG-эндпоинту (JWT)
 * @param {string} projectId
 * @param {string} ragId
 * @param {Object} data - OpenAI-совместимое тело: { model?, messages: [], temperature?, max_tokens? }
 */
export async function sendProjectChatMessage(projectId, ragId, data) {
  return await apiRequest(`${ragBasePath(projectId, ragId)}/chat/completions/jwt`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Агрегированная сводка проекта для dashboard
 * @param {string} projectId
 */
export async function getProjectSummary(projectId) {
  return apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/summary`, {
    method: 'GET',
  });
}

/**
 * Общая история чат-сообщений
 * @param {string} projectId
 * @param {string} ragId
 * @param {Object} [params]
 * @param {number} [params.limit]
 * @param {number} [params.offset]
 * @param {string} [params.channel] - 'api' | 'telegram'
 */
export async function getChatHistory(projectId, ragId, params = {}) {
  const url = new URL(`${ragBasePath(projectId, ragId)}/chat/history`);
  if (params.limit != null) url.searchParams.set('limit', params.limit);
  if (params.offset != null) url.searchParams.set('offset', params.offset);
  if (params.channel) url.searchParams.set('channel', params.channel);
  return apiRequest(url.toString(), { method: 'GET' });
}

// --- Операции со схемой хранилища ---

/**
 * Обновить настройки хранилища (схемы, таблицы, лимит и т.д.)
 * @param {string} projectId
 * @param {number} storageId
 * @param {Object} data
 */
export async function updateProjectStorage(projectId, storageId, data) {
  return apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/storages/${storageId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Отвязать таблицу от привязки project + storage (allowed_tables)
 * @param {string} projectId
 * @param {number|string} storageId
 * @param {string} tableName
 */
export async function unlinkProjectStorageTable(projectId, storageId, tableName) {
  const encodedTableName = encodeURIComponent(tableName);
  return apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/storages/${storageId}/tables/${encodedTableName}`, {
    method: 'DELETE',
  });
}

/**
 * Доступные таблицы в БД хранилища
 * @param {string} projectId
 */
export async function getAvailableTables(projectId) {
  return apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/storages/available-tables`, {
    method: 'GET',
  });
}

/**
 * Индексировать схему БД
 * @param {string} projectId
 * @param {Object} [data]
 * @param {string[]} [data.tables] - список конкретных таблиц для индексации
 */
export async function indexStorageSchema(projectId, data) {
  return apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/storages/index`, {
    method: 'POST',
    ...(data ? { body: JSON.stringify(data) } : {}),
  });
}

/**
 * Список таблиц проекта с дедубликацией и schema-метаданными
 * @param {string} projectId
 */
export async function getStorageSchema(projectId) {
  return apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/storages/schema`, {
    method: 'GET',
  });
}

/**
 * Обновить комментарий к проиндексированной таблице
 * @param {string} projectId
 * @param {number|string} chunkId
 * @param {Object} data - { user_comment?: string, column_comments?: Record<string, string|null> }
 */
export async function updateSchemaChunk(projectId, chunkId, data) {
  return apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/storages/schema/${chunkId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Запись пользовательских строк в таблицу проектной БД
 * @param {string} projectId
 * @param {Object} data - { table_name: string, rows: Object[] }
 */
export async function writeProjectRows(projectId, data) {
  return apiRequest(`${DTCORE_API_BASE_URL}/api/v1/projects/${projectId}/storages/write`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

